import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, forkJoin, Observable, of, Subject, throwError} from 'rxjs';
import {catchError, map, mergeMap, shareReplay} from 'rxjs/operators';
import fixArray from '../../../../../utils/fixArray';
import {IHolidayEditableReceiptField} from '../../../../shared/interfaces/iholiday-editable-receipt-field';
import {IRemoteQuotationResponse} from '../../../../shared/interfaces/iremote-quotation-response';
import {EnvironmentService} from '../../../../shared/services/environment.service';
import {ProjectService} from '../../../../shared/services/project.service';
import {IHolidayInsurancePriceParams} from '../interfaces/iholiday-insurance-price-params';
import {IHolidayLive} from '../interfaces/iholiday-live';
import {IHolidayLiveParams} from '../interfaces/iholiday-live-params';
import {IHolidayLiveRoom} from '../interfaces/iholiday-live-room';
import {IHolidayLiveRoomCharge} from '../interfaces/iholiday-live-room-charge';
import {IHolidayLiveSolution} from '../interfaces/iholiday-live-solution';
import {IHolidayLiveTransport} from '../interfaces/iholiday-live-transport';
import {IHolidayQuotationParams} from '../interfaces/iholiday-quotation-params';
import {IHolidayReservationCancelParams} from '../interfaces/iholiday-reservation-cancel-params';
import {IHolidayReservationConfirmParams} from '../interfaces/iholiday-reservation-confirm-params';
import {IHolidayReservationCreateParams} from '../interfaces/iholiday-reservation-create-params';
import {FormatDatePipe} from 'src/app/modules/shared/pipes/format-date.pipe';
import * as dayjs from 'dayjs';
import {BookingService} from 'src/app/modules/shared/services/booking.service';
import {IHolidayDetailFlight} from '../../holiday-detail-page/interfaces/iholiday-detail-flight';

@Injectable({
  providedIn: 'root',
})
export class HolidayBookingService {
  bookingFields: IHolidayEditableReceiptField[] = null;
  bookingFields$ = new BehaviorSubject<IHolidayEditableReceiptField[]>(
    this.bookingFields
  );

  selectedCity: any = null;
  selectedCity$ = new BehaviorSubject<any>(this.selectedCity);

  holidayDetails: any = null;
  holidayDetails$ = new BehaviorSubject<any>(this.holidayDetails);

  selectedDepartureDate: Date = null;
  selectedDepartureDate$ = new BehaviorSubject<Date>(this.selectedDepartureDate);

  selectedStation: any = null;
  selectedStation$ = new BehaviorSubject<any>(this.selectedStation);

  selectedSolution: any = null;
  selectedSolution$ = new BehaviorSubject<any>(this.selectedSolution);

  private holidayDetailLoadingStatus: { isLoadingDetail: boolean, isLoadingLivePrices: boolean, isDebounceing: boolean } = {
    isLoadingDetail: false,
    isLoadingLivePrices: false,
    isDebounceing: false
  };

  public holidayDetailLoadingStatus$: BehaviorSubject<{ isLoadingDetail: boolean, isLoadingLivePrices: boolean, isDebounceing: boolean }> = new BehaviorSubject(this.holidayDetailLoadingStatus);

  public departureDateAccordionTrigger$: Subject<boolean> = new Subject<boolean>();


  constructor(
    private _http: HttpClient,
    private _environmentService: EnvironmentService,
    private _projectService: ProjectService,
    private _formatDatePipe: FormatDatePipe,
    private _bookingService: BookingService
  ) {
  }

  httpCache: Map<string, Observable<any>> = new Map<string, Observable<any>>();

  setData(
    key:
      | 'bookingFields'
      | 'selectedSolution'
      | 'holidayDetails'
      | 'selectedDepartureDate',
    value: any
  ): void {
    this[key] = value;
    this[`${key}$`].next(this[key]);
  }

  public setDetailLoadingStatus(isLoadingDetail: boolean = false, isLoadingLivePrices: boolean = false, isDebounceing: boolean = false): void {
    this.holidayDetailLoadingStatus$.next({isLoadingDetail, isLoadingLivePrices, isDebounceing});
  }

  //  Effettua una liveAlpi ed aggiunge informazioni in essa presenti (volo e prezzo aggiornato) alla corrispondente soluzione di dettaglio (passata come argomento)
  getSolutionAdditionalLiveInfo(detailSolution) {
    const params = this.createLiveParams(detailSolution);
    //  A seguito di segnalazione #29800 è stato stabilito che nella fattispecie del priceShownMustBeLive onlyNetQuotes debba essere true
    if(detailSolution.priceShownMustBeLive) {
      params.request.onlyNetQuotes = true;
    }

    return this.getFormattedHolidayLive(params)
      .toPromise()
      .then((holidayLive) => {
        if (!holidayLive) {
          detailSolution.liveError = true;
          console.warn(
            'Errore nella liveAlpi per la soluzione ',
            detailSolution
          );
          throw new Error('errore nella livealpi');
        }
        const liveSolutions = holidayLive?.solutions || null;

        const matchingSolution = this.findMatchingSolution(
          detailSolution,
          liveSolutions
        );

        detailSolution.journey = detailSolution.flights
          ? this.getSolutionLiveFlightInfo(detailSolution, matchingSolution)
          : null;

        detailSolution.totalPrice =
          this.getSolutionLivePrice(matchingSolution) ||
          detailSolution.totalPrice;
      });
  }

  //  Crea parametri per la liveAlpi
  createLiveParams(sol) {
    return {
      request: {
        pluginSource: sol.pluginSource,
        ages: this._bookingService.getAgesArray(),
        departureDate: this._formatDatePipe.transform(
          sol.dateFrom,
          'simpleYearMonthDate'
        ),
        departurePlace: sol.airport,
        idSolution: sol.id || null,
        sourceId: sol.source,
        topId: sol.parentTop,
        onlyNetQuotes:
          sol.hasNetQuote && (this.holidayDetails?.isCustom)
            ? sol.hasNetQuote
            : false,
        stationId: null,
      },
    };
  }

  //  Restituisce informazioni relative al volo presenti nella chiamata liveAlpi
  getSolutionLiveFlightInfo(detailSolution, matchingSolution) {
    const departureFlight = detailSolution.flights.find(
      (flight) => flight.type === 'ANDATA'
    );
    const returnFlight = detailSolution.flights.find(
      (flight) => flight.type === 'RITORNO'
    );
    return {
      outward: this.formatJourneyInfo(
        departureFlight,
        matchingSolution?.transports
      ),
      return: this.formatJourneyInfo(
        returnFlight,
        matchingSolution?.transports
      ),
    };
  }

  //  Restituisce il prezzo più basso fra quelli presenti fra i risultati di una liveAlpi
  getSolutionLivePrice(matchingSolution) {
    let bestPrice = matchingSolution?.rooms[0].price;
    if (matchingSolution.rooms?.length > 1) {
      matchingSolution.rooms.forEach((room) => {
        if (room.price < bestPrice) {
          bestPrice = room.price;
        }
      });
    }
    return bestPrice;
  }

  //  Restituisce true se sono presenti più voli dallo stesso aeroporto nella stessa giornata
  multipleFlightsOnSameDay(airportCodeFromDetail, liveSolutions) {
    const allDepartingHours = [];
    liveSolutions.forEach((sol) => {
      const outwardFlight = sol?.transports?.find((flight) =>
        this.areAirportCodesEqual(flight.departureApt, airportCodeFromDetail)
      );
      if (
        outwardFlight?.departureHour &&
        !allDepartingHours.includes(outwardFlight.departureHour)
      ) {
        allDepartingHours.push(outwardFlight.departureHour);
      }
    });
    return allDepartingHours?.length > 1;
  }

  //  Passato l'orario di partenza proveniente dalla chiamata di dettaglio, il codice aeroporto di partenza e la lista delle soluzioni live, trova l'orario corrispondente
  getMatchingDepartureHour(
    detailDepartureHour,
    airportCodeFromDetail,
    liveSolutions
  ) {
    const detailHourInMinutes =
      this.getMinutesFromStringTime(detailDepartureHour);
    let diff = Infinity;
    let matchingHour;
    liveSolutions.forEach((sol) => {
      const outwardFlight = sol.transports.find((flight) =>
        this.areAirportCodesEqual(flight.departureApt, airportCodeFromDetail)
      );
      const liveHourInMinutes = this.getMinutesFromStringTime(
        outwardFlight.departureHour
      );
      if (Math.abs(liveHourInMinutes - detailHourInMinutes) < diff) {
        diff = Math.abs(liveHourInMinutes - detailHourInMinutes);
        matchingHour = outwardFlight.departureHour;
      }
    });
    return matchingHour;
  }

  //  Trasforma un orario fornito come stringa nel formato (hh:mm) in minuti trascorsi dalla mezzanotte a quell'orario (es: 10:30 => 630)
  getMinutesFromStringTime(stringTime) {
    return (
      Number(stringTime.substring(0, 2)) * 60 +
      Number(stringTime.substring(3, 5))
    );
  }

  // Passati come parametri la solution attuale (derivante dalla chiamata di dettaglio) ed il risultato della liveAlpi, restituisce la soluzione della liveAlpi corrispondente
  findMatchingSolution(detailSolution, liveSolutions) {
    //  Se la liveAlpi riporta una sola solution restituisco quella
    if (liveSolutions.length === 1) {
      return liveSolutions[0];
    }

    //  Se la liveAlpi riporta più solutions può essere perché dall'una all'altra cambiano: 1) orario dei voli 2) classe del volo 3) trattamento
    //  È essenziale che si faccia il match con la solution avente lo stesso volo. Gli altri parametri possono essere momentaneamente trascurati.

    let matchingSolution;

    if (detailSolution.flights) {
      const departureFlightFromDetail = detailSolution.flights.find(
        (flight) => flight.type.toLowerCase() === 'andata'
      );
      const airportCodeFromDetail =
        departureFlightFromDetail.departureAirport.code;

      //  Se alcune soluzioni differiscono in base all'orario del volo
      if (this.multipleFlightsOnSameDay(airportCodeFromDetail, liveSolutions)) {
        console.warn('Trovate soluzioni multiple nella stessa giornata');
        const detailDepartureHour = this._formatDatePipe.transform(
          departureFlightFromDetail.departureDate,
          'hhmmss'
        );
        const matchingHour = this.getMatchingDepartureHour(
          detailDepartureHour,
          airportCodeFromDetail,
          liveSolutions
        );
        liveSolutions = liveSolutions.filter((sol) => {
          const outwardFlight = sol.transports.find((flight) =>
            this.areAirportCodesEqual(
              flight.departureApt,
              airportCodeFromDetail
            )
          );
          return outwardFlight.departureHour === matchingHour ? true : false;
        });
      }
    }

    let currentPrice = detailSolution.totalPrice;
    let currentRoomBookingCode = detailSolution.rooms[0]?.title;
    let priceDiff = null;

    liveSolutions.forEach((sol) => {
      if (sol?.rooms) {
        sol?.rooms.forEach((room) => {
          if (
            room?.bookingCode.toLowerCase() ===
            currentRoomBookingCode?.toLowerCase()
          ) {
            let diff = Math.abs(room.price - currentPrice);
            if (priceDiff == null || diff < priceDiff) {
              //  In presenza di diverse soluzioni faccio il match prendendo quella col prezzo più vicino
              priceDiff = diff;
              matchingSolution = sol;
            }
          }
        });
      }
    });

    // restituisco soluzione corrispondente o, in sua assenza, la prima soluzione
    return matchingSolution || liveSolutions[0];
  }

  //  Passati l'oggetto flight derivante dalla chiamata di dettaglio e la lista dei voli derivante dalla liveAlpi crea un oggetto journey che mappa tutte le informazioni necessarie
  formatJourneyInfo(flightDetail: IHolidayDetailFlight, flightListLive: any): any {
    const departureAirport = flightDetail.departureAirport;
    const arrivalAirport = flightDetail.arrivalAirport;
    const stopOver = flightDetail.stopOver;
    const stopOverDuration = flightDetail.stopOverDuration;
    const departureDateFromLiveOrBatch = flightListLive?.find(
      (flight) =>
        this.areAirportCodesEqual(
          flight?.departureApt,
          departureAirport.code
        ) ||
        flight.tratta?.toLowerCase() === flightDetail?.type.toLowerCase()
    )?.departureDate || flightDetail.departureDate;
    const arrivalDateFromLiveOrBatch = flightListLive?.find(
      (flight) =>
        this.areAirportCodesEqual(
          flight?.arrivalApt,
          arrivalAirport.code
        ) ||
        flight.tratta?.toLowerCase() === flightDetail?.type.toLowerCase()
    )?.arrivalDate || flightDetail.arrivalDate;
    const departureHour =
      flightListLive
        ?.find(
          (flight) =>
            this.areAirportCodesEqual(
              flight?.departureApt,
              departureAirport.code
            ) ||
            flight.tratta?.toLowerCase() === flightDetail?.type.toLowerCase()
        )
        ?.departureHour.substring(0, 5) || '';
    const arrivalHour =
      flightListLive
        ?.find(
          (flight) =>
            this.areAirportCodesEqual(
              flight?.arrivalApt,
              arrivalAirport.code
            ) ||
            flight.tratta?.toLowerCase() === flightDetail?.type.toLowerCase()
        )
        ?.arrivalHour.substring(0, 5) || '';
    const departureDate = new Date(departureDateFromLiveOrBatch) || '';
    //  Data di arrivo: se l'orario dell'arrivo è precedente alla partenza significa che si riferisce al giorno successivo
    const landingNextDay = (arrivalHour || departureHour) ? Number(arrivalHour.substring(0, 2)) <= Number(departureHour.substring(0, 2)) : false;
    const arrivalDate = (arrivalDateFromLiveOrBatch) ? new Date(arrivalDateFromLiveOrBatch) : (new Date(dayjs(departureDate)?.add(landingNextDay ? 1 : 0, 'd').toDate()) || '');
    return {
      departureAirport,
      arrivalAirport,
      departureHour,
      arrivalHour,
      departureDate,
      arrivalDate,
      stopOver,
      stopOverDuration,
    };
  }

  //  Fornito il codice di due aeroporti, potenzialmente formattati in maniera diversa, controlla che siano uguali
  areAirportCodesEqual(aptCode1, aptCode2): boolean {
    if (!aptCode1 || !aptCode2) {
      return false;
    }
    return (
      aptCode1.toString().substring(0, 3).toLowerCase() ===
      aptCode2.toString().substring(0, 3).toLowerCase()
    );
  }

  formatLiveSolution(solution: any): IHolidayLiveSolution {
    return {
      departureDate: new Date(solution.DepartureDate),
      nights: solution.Nights,
      bestRoom: this.getBestRoom(solution.Rooms_Collection),
      rooms: solution.Rooms_Collection
        ? fixArray(solution.Rooms_Collection.Room).map((e) =>
          this.formatLiveRoom(e)
        )
        : undefined,
      transports: solution.Transports
        ? fixArray(solution.Transports.Transport).map((e) =>
          this.formatLiveTransport(e)
        )
        : undefined,
    };
  }

  formatLiveTransport(transport: any): IHolidayLiveTransport {
    return {
      class: transport?.Classe,
      classCode: transport?.ClasseCode,
      company: transport?.Company,
      stations: transport?.Stations?.sort(
        (a, b) => a.Progressive - b.Progressive
      ),
      title: transport?.Title,
      arrivalApt: transport?.ArrivalApt,
      arrivalHour: transport?.ArrivalHour,
      departureHour: transport?.DepartureHour,
      tratta: transport?.Tratta,
      departureDate: transport?.DepartureDate,
      departureApt: transport?.DepartureApt,
      number: transport.Number || null
    };
  }

  formatCharge(charge: any, index: number): IHolidayLiveRoomCharge {
    return {
      id: index,
      title: charge.Description,
      description: charge.Notes,
      included: charge.Included,
      price: charge.Price ? parseFloat(charge.Price) : 0,
      quantity: charge.Quantity ? parseInt(charge.Quantity, 10) : undefined,
      type: charge.Type,
      bookingCode: charge.BookingCode ? charge.BookingCode : undefined,
      icon: charge.Icon ? charge.Icon : undefined,
      onDemand: charge.OnDemand,
      url: charge.Url,
      attachmentUrl: charge.Attachment,
      required: charge.Required,
      items: charge.Items
        ? fixArray(charge.Items).map((e, indexItem) =>
          this.formatChargeItems(e, indexItem)
        )
        : undefined,
      marginality: charge.Marginality ? parseFloat(charge.Marginality) : 0,
      marginalityCoeff: charge.MarginalityCoeff
        ? charge.MarginalityCoeff
        : null,
      payableLocally: charge.PayableLocally,
      selected: false,
      openLinkMore: false,
    };
  }

  formatChargeItems(chargeItems: any, index: number): IHolidayLiveRoomCharge {
    return {
      id: index,
      title: chargeItems.Description,
      description: chargeItems.Notes,
      included: chargeItems.Included,
      price: chargeItems.Price ? parseFloat(chargeItems.Price) : 0,
      quantity: chargeItems.Quantity ? parseInt(chargeItems.Quantity, 10) : 1,
      payableLocally: chargeItems.PayableLocally,
      type: chargeItems.Type,
      onDemand: chargeItems.OnDemand,
      bookingCode: chargeItems.BookingCode
        ? chargeItems.BookingCode
        : undefined,
      icon: chargeItems.Icon ? chargeItems.Icon : undefined,
      limit: chargeItems.Limit ? parseInt(chargeItems.Limit, 10) : 0,
      marginality: chargeItems.Marginality
        ? parseFloat(chargeItems.Marginality)
        : 0,
      marginalityCoeff: chargeItems.MarginalityCoeff
        ? chargeItems.MarginalityCoeff
        : null,
      required: chargeItems.Required,
      openLinkMore: false,
    };
  }

  formatLiveRoom(room: any): IHolidayLiveRoom {
    return {
      availability: room.Availability,
      adults: room.AdultsNum,
      basis: room.Basis
        ? {
          id: room.Basis.Id,
          code: room.Basis.Code,
          name: room.Basis.Name,
        }
        : undefined,
      bookingCode: room.BookingCode,
      children: room.ChildsNum,
      charges: room.DetailTaxes
        ? fixArray(room.DetailTaxes).map((e, index) =>
          this.formatCharge(e, index)
        )
        : [],
      source: room.Source,
      title: room.Description,
      infants: room.InfantsNum,
      occupants: room.Occupants,
      price: parseFloat(room.MKPriceCP),
      basePrice: parseFloat(room.Price),
      tax: room.Tax ? parseFloat(room.Tax) : 0,
      marginality: room.Marginality ? parseFloat(room.Marginality) : 0,
      type: room.TypeExtended,
    };
  }

  formatHolidayLive(holidayLive: any): IHolidayLive {
    return {
      bookingCode: holidayLive.BookingCode,
      solutions: holidayLive.SolutionsList.Solution
        ? fixArray(holidayLive.SolutionsList.Solution).map((e) =>
          this.formatLiveSolution(e)
        )
        : undefined,
      source: holidayLive.Source,
    };
  }

  getFormattedHolidayLive(
    searchParams: IHolidayLiveParams
  ): Observable<IHolidayLive> {
    return this.getHolidayLive(searchParams).pipe(
      map((e) => (e ? this.formatHolidayLive(e) : undefined)),
      map((e) => {
        if (
          !e ||
          !e.solutions ||
          e.solutions.map((x) => x.rooms).filter((x) => x).length === 0
        ) {
          return undefined;
        }
        return e;
      }),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }

  getFormattedInsurancePrice(
    searchParams: IHolidayInsurancePriceParams
  ): Promise<number> {
    return this.getInsurancePrice(searchParams)
      .pipe(
        map((e) => {
          const price = e ? parseInt(e, 10) : undefined;
          return price && !isNaN(price) ? price : undefined;
        }),
        shareReplay({bufferSize: 1, refCount: true}),
        catchError((err) => throwError(err))
      )
      .toPromise();
  }

  // Restituisce lista di assicurazioni
  getInsuranceOptions(params: any): Observable<any> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap((e) => {
        const url = `${e[0].otoApi2UrlDynamic}holiday/nauth/${e[1]}/insuranceService`;
        return this._http.post<any>(url, params);
      }),
      map((e) => e.result),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }

  getNewQuotation(params, slim = false, agencyApiKey = null) {
    return forkJoin([
      this._environmentService.getEnvironment(),
      agencyApiKey ? of(agencyApiKey) : this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap((e) => {
        const baseUrl = e[0].otoApi2UrlDynamic;
        const apiKey = e[1];
        const url = `${baseUrl}holiday/nauth/${apiKey}/quotation${slim ? 'Slim' : ''}`;
        return this._http.post<any>(url, params);
      }),
      map((e) => e.result),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }

  getQuotationSlim(params) {
    return this.getNewQuotation(params, true);
  }

  // Restituisce gli additional services
  getAdditionalServices(params: any): Observable<any> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap((e) => {
        const url = `${e[0].otoApi2UrlDynamic}holiday/nauth/${e[1]}/additionalService`;
        return this._http.post<any>(url, params);
      }),
      map((e) => e.result),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }

  getHolidayQuotation(
    params: IHolidayQuotationParams
  ): Observable<IRemoteQuotationResponse> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap((e) => {
        const url = `${e[0].searchUrl}/index.php/holiday/${e[1]}/user/book/live`;
        return this._http.post<any>(url, params);
      }),
      map((e) => e.Result?.QuotationResponse),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }

  getHolidayLive(searchParams: IHolidayLiveParams): Observable<any> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap((e) => {
        const url = `${e[0].searchUrl}/index.php/holiday/${e[1]}/user/book/livealpi`;
        const key = url + JSON.stringify(searchParams);
        if (!this.httpCache.get(key)) {
          this.httpCache.set(
            key,
            this._http.post<any>(url, searchParams).pipe(
              shareReplay({
                bufferSize: 1,
                refCount: true,
              })
            )
          );
        }
        return this.httpCache.get(key);
      }),
      map((e) => (e.Result ? e.Result.GetAdditionalRoomsResponse : undefined)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }

  getInsurancePrice(
    searchParams: IHolidayInsurancePriceParams
  ): Observable<any> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap((e) => {
        const url = `${e[0].otoApi2UrlDynamic}ancillary/nauth/${e[1]}/insurance/check`;
        const key = url + JSON.stringify(searchParams);
        if (!this.httpCache.get(key)) {
          this.httpCache.set(
            key,
            this._http.post<any>(url, searchParams).pipe(
              shareReplay({
                bufferSize: 1,
                refCount: true,
              })
            )
          );
        }
        return this.httpCache.get(key);
      }),
      map((e) => (e.result && !e.error ? e.result : undefined)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }

  createReservation(params: IHolidayReservationCreateParams): Observable<any> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap((e) => {
        const url = `${e[0].otoApi2UrlDynamic}holiday/nauth/${e[1]}/reservation`;
        // holiday/nauth/${e[1]}/quotation
        return this._http.post<any>(url, params);
      }),
      map((e: any) => e.result?.reservationResponseV2),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }

  //
  confirmReservation(
    params: IHolidayReservationConfirmParams
  ): Observable<IRemoteQuotationResponse> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap((e) => {
        const url = `${e[0].otoApi2UrlDynamic}holiday/nauth/${e[1]}/confirmReservation`;
        return this._http.post<any>(url, params);
      }),
      map((e: any) => e.result?.confirmReservationResponseV2),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }

  //  Per adesso dovrebbe rimanere fuori dal flusso
  //TODO da testare se e quando sarà integrata!
  confirmOption(
    params: IHolidayReservationConfirmParams
  ): Observable<IRemoteQuotationResponse> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap((e) => {
        const url = `${e[0].otoApi2UrlDynamic}holiday/nauth/${e[1]}/confirmOption`;
        return this._http.post<any>(url, params);
      }),
      map((e: any) => e.result?.confirmOptionResponseV2),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }

  cancelReservation(params: IHolidayReservationCancelParams): Observable<any> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap((e) => {
        const url = `${e[0].otoApi2UrlDynamic}holiday/nauth/${e[1]}/cancelReservation`;
        return this._http.post<any>(url, params);
      }),
      map((e: any) => e.result?.cancelReservationResponse),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }

  getBestRoom(roomsCollection: any): IHolidayLiveRoom {
    return roomsCollection
      ? fixArray(roomsCollection.Room)
        .map((e) => this.formatLiveRoom(e))
        .reduce((a, b) => {
          if (!a) {
            return b;
          }
          return b.price < a.price ? b : a;
        })
      : undefined;
  }

  //  restituisce i dati di una quotation in base al suo id. Da capire meglio.
  getQuotation(quotationId, quotationType): Observable<any> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      of(quotationId),
      this._projectService.getAgencyApikey(),
      of(quotationType),
    ]).pipe(
      mergeMap((e) => {
        const url = `${e[0].quotation}/nauth/${e[1]}?key=${e[2]}&type=${e[3]}`;
        return this._http.get<any>(url);
      }),
      map((quotation) => quotation.result),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError((err) => throwError(err))
    );
  }
}
